import React, { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
    Target,
    Plus,
    Loader2,
    X,
    Trash2,
    Trophy,
    Sparkles,
    Building2,
    Clock,
} from "lucide-react";
import { api } from "../lib/api";

const STAGES = [
    { key: "prospect", label: "Prospect", accent: "#71717A" },
    { key: "won", label: "Won", accent: "#10B981" },
    { key: "in_progress", label: "In progress", accent: "#0066FF" },
    { key: "lost", label: "Lost", accent: "#EF4444" },
];

const STAGE_COLOR = Object.fromEntries(STAGES.map((s) => [s.key, s.accent]));

const currency = (n) => `£${(Number(n) || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

function daysUntil(iso) {
    if (!iso) return null;
    const days = Math.round((new Date(iso).getTime() - Date.now()) / 86400000);
    return days;
}

function ProgressRing({ percent = 0, size = 128 }) {
    const stroke = 10;
    const r = (size - stroke) / 2;
    const c = 2 * Math.PI * r;
    const dash = (percent / 100) * c;
    return (
        <svg width={size} height={size} className="-rotate-90">
            <defs>
                <linearGradient id="pr-grad" x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0%" stopColor="#0066FF" />
                    <stop offset="100%" stopColor="#FF5500" />
                </linearGradient>
            </defs>
            <circle cx={size / 2} cy={size / 2} r={r} stroke="rgba(255,255,255,0.06)" strokeWidth={stroke} fill="none" />
            <motion.circle
                cx={size / 2}
                cy={size / 2}
                r={r}
                stroke="url(#pr-grad)"
                strokeWidth={stroke}
                fill="none"
                strokeLinecap="round"
                initial={{ strokeDasharray: `0 ${c}` }}
                animate={{ strokeDasharray: `${dash} ${c}` }}
                transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
            />
        </svg>
    );
}

// ─── Create Goal Modal ─────────────────────────────────────────────
function CreateGoalModal({ open, onClose, onCreated }) {
    const [title, setTitle] = useState("");
    const [target, setTarget] = useState("");
    const [count, setCount] = useState("");
    const [weeks, setWeeks] = useState(6);
    const [loading, setLoading] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (!title.trim()) return;
        setLoading(true);
        try {
            const deadline = new Date(Date.now() + weeks * 7 * 86400000).toISOString();
            const { data } = await api.post("/goals", {
                title,
                target_value: Number(target) || 0,
                target_count: count ? Number(count) : null,
                deadline,
            });
            onCreated(data);
            setTitle(""); setTarget(""); setCount(""); setWeeks(6);
            onClose();
        } finally {
            setLoading(false);
        }
    };

    if (!open) return null;
    return (
        <div className="fixed inset-0 z-50 grid place-items-center px-4" data-testid="goal-create-modal">
            <div className="absolute inset-0 bg-black/80 backdrop-blur-md" onClick={onClose} />
            <motion.form
                onSubmit={submit}
                initial={{ scale: 0.96, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                className="relative w-full max-w-lg rounded-2xl border border-white/10 bg-[#0E0E11] p-6"
            >
                <div className="flex items-center justify-between mb-5">
                    <div className="flex items-center gap-2">
                        <Target className="h-4 w-4 text-[#0066FF]" />
                        <h3 className="font-display text-lg">New goal</h3>
                    </div>
                    <button type="button" data-testid="goal-create-close" onClick={onClose} className="p-1.5 rounded-lg hover:bg-white/[0.06]">
                        <X className="h-4 w-4" />
                    </button>
                </div>

                <label className="block mb-3">
                    <span className="text-xs text-zinc-400 mb-1.5 block">Title</span>
                    <input
                        data-testid="goal-create-title"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        placeholder="e.g. £20k in 6 weeks from 10 clients"
                        autoFocus
                        className="w-full bg-[#08080A] border border-white/[0.08] rounded-xl px-3 py-2.5 text-sm focus:border-[#0066FF] outline-none"
                    />
                </label>

                <div className="grid grid-cols-3 gap-3">
                    <label className="block">
                        <span className="text-xs text-zinc-400 mb-1.5 block">Target £</span>
                        <input
                            data-testid="goal-create-target"
                            type="number"
                            min="0"
                            value={target}
                            onChange={(e) => setTarget(e.target.value)}
                            placeholder="20000"
                            className="w-full bg-[#08080A] border border-white/[0.08] rounded-xl px-3 py-2.5 text-sm focus:border-[#0066FF] outline-none font-mono"
                        />
                    </label>
                    <label className="block">
                        <span className="text-xs text-zinc-400 mb-1.5 block">Client target</span>
                        <input
                            data-testid="goal-create-count"
                            type="number"
                            min="0"
                            value={count}
                            onChange={(e) => setCount(e.target.value)}
                            placeholder="10"
                            className="w-full bg-[#08080A] border border-white/[0.08] rounded-xl px-3 py-2.5 text-sm focus:border-[#0066FF] outline-none font-mono"
                        />
                    </label>
                    <label className="block">
                        <span className="text-xs text-zinc-400 mb-1.5 block">Weeks</span>
                        <input
                            data-testid="goal-create-weeks"
                            type="number"
                            min="1"
                            value={weeks}
                            onChange={(e) => setWeeks(Number(e.target.value))}
                            className="w-full bg-[#08080A] border border-white/[0.08] rounded-xl px-3 py-2.5 text-sm focus:border-[#0066FF] outline-none font-mono"
                        />
                    </label>
                </div>

                <div className="mt-5 flex justify-end gap-2">
                    <button type="button" onClick={onClose} className="text-xs px-4 py-2 rounded-full border border-white/[0.08] hover:bg-white/[0.04]">Cancel</button>
                    <button
                        data-testid="goal-create-submit"
                        disabled={loading}
                        className="text-xs px-4 py-2 rounded-full bg-white text-black font-medium hover:bg-zinc-200 disabled:opacity-50 inline-flex items-center gap-2"
                    >
                        {loading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Target className="h-3 w-3" />}
                        Create goal
                    </button>
                </div>
            </motion.form>
        </div>
    );
}

// ─── Create Opportunity Modal ──────────────────────────────────────
function CreateOppModal({ open, onClose, onCreated, goalId, clients, onClientCreated }) {
    const [name, setName] = useState("");
    const [value, setValue] = useState("");
    const [clientId, setClientId] = useState("");
    const [addingNew, setAddingNew] = useState(false);
    const [newCompany, setNewCompany] = useState("");
    const [newWebsite, setNewWebsite] = useState("");
    const [loading, setLoading] = useState(false);

    const reset = () => {
        setName(""); setValue(""); setClientId("");
        setAddingNew(false); setNewCompany(""); setNewWebsite("");
    };

    const submit = async (e) => {
        e.preventDefault();
        if (!name.trim()) return;
        setLoading(true);
        try {
            let linkedClientId = clientId || null;
            // create new company first if needed
            if (addingNew && newCompany.trim()) {
                const { data: newClient } = await api.post("/clients", {
                    name: newCompany.trim(),
                    website: newWebsite.trim(),
                    hosting_package: "Prospect",
                    monthly_billing: 0,
                    services: [],
                    renewal_status: "ok",
                });
                linkedClientId = newClient.id;
                onClientCreated?.(newClient);
            }
            const { data } = await api.post("/opportunities", {
                goal_id: goalId,
                name,
                value: Number(value) || 0,
                client_id: linkedClientId,
            });
            onCreated(data);
            reset();
            onClose();
        } finally {
            setLoading(false);
        }
    };

    const handleClientChange = (val) => {
        if (val === "__new__") {
            setAddingNew(true);
            setClientId("");
        } else {
            setAddingNew(false);
            setClientId(val);
        }
    };

    if (!open) return null;
    return (
        <div className="fixed inset-0 z-[60] grid place-items-center px-4" data-testid="opp-create-modal">
            <div className="absolute inset-0 bg-black/80 backdrop-blur-md" onClick={onClose} />
            <motion.form
                onSubmit={submit}
                initial={{ scale: 0.96, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                className="relative w-full max-w-lg rounded-2xl border border-white/10 bg-[#0E0E11] p-6"
            >
                <div className="flex items-center justify-between mb-5">
                    <div className="flex items-center gap-2">
                        <Sparkles className="h-4 w-4 text-[#FF5500]" />
                        <h3 className="font-display text-lg">Add opportunity</h3>
                    </div>
                    <button type="button" data-testid="opp-create-close" onClick={onClose} className="p-1.5 rounded-lg hover:bg-white/[0.06]">
                        <X className="h-4 w-4" />
                    </button>
                </div>

                <label className="block mb-3">
                    <span className="text-xs text-zinc-400 mb-1.5 block">Prospect / opportunity name</span>
                    <input
                        data-testid="opp-create-name"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="e.g. Cedar Views summer retainer"
                        autoFocus
                        className="w-full bg-[#08080A] border border-white/[0.08] rounded-xl px-3 py-2.5 text-sm focus:border-[#0066FF] outline-none"
                    />
                </label>

                <div className="grid grid-cols-2 gap-3">
                    <label className="block">
                        <span className="text-xs text-zinc-400 mb-1.5 block">Value £</span>
                        <input
                            data-testid="opp-create-value"
                            type="number"
                            min="0"
                            value={value}
                            onChange={(e) => setValue(e.target.value)}
                            placeholder="2000"
                            className="w-full bg-[#08080A] border border-white/[0.08] rounded-xl px-3 py-2.5 text-sm focus:border-[#0066FF] outline-none font-mono"
                        />
                    </label>
                    <label className="block">
                        <span className="text-xs text-zinc-400 mb-1.5 block">Company</span>
                        <select
                            data-testid="opp-create-client"
                            value={addingNew ? "__new__" : clientId}
                            onChange={(e) => handleClientChange(e.target.value)}
                            className="w-full bg-[#08080A] border border-white/[0.08] rounded-xl px-3 py-2.5 text-sm focus:border-[#0066FF] outline-none"
                        >
                            <option value="">— No company —</option>
                            {clients.map((c) => (
                                <option key={c.id} value={c.id}>{c.name}</option>
                            ))}
                            <option value="__new__">+ Add new company…</option>
                        </select>
                    </label>
                </div>

                {addingNew && (
                    <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        className="mt-3 grid grid-cols-2 gap-3 overflow-hidden"
                    >
                        <label className="block">
                            <span className="text-xs text-zinc-400 mb-1.5 block">New company name</span>
                            <input
                                data-testid="opp-new-company-name"
                                value={newCompany}
                                onChange={(e) => setNewCompany(e.target.value)}
                                placeholder="Acme Ltd"
                                className="w-full bg-[#08080A] border border-[#0066FF]/40 rounded-xl px-3 py-2.5 text-sm focus:border-[#0066FF] outline-none"
                            />
                        </label>
                        <label className="block">
                            <span className="text-xs text-zinc-400 mb-1.5 block">Website (optional)</span>
                            <input
                                data-testid="opp-new-company-website"
                                value={newWebsite}
                                onChange={(e) => setNewWebsite(e.target.value)}
                                placeholder="acme.com"
                                className="w-full bg-[#08080A] border border-white/[0.08] rounded-xl px-3 py-2.5 text-sm focus:border-[#0066FF] outline-none"
                            />
                        </label>
                    </motion.div>
                )}

                <div className="mt-5 flex justify-end gap-2">
                    <button type="button" onClick={onClose} className="text-xs px-4 py-2 rounded-full border border-white/[0.08] hover:bg-white/[0.04]">Cancel</button>
                    <button
                        data-testid="opp-create-submit"
                        disabled={loading || (addingNew && !newCompany.trim())}
                        className="text-xs px-4 py-2 rounded-full bg-white text-black font-medium hover:bg-zinc-200 disabled:opacity-50 inline-flex items-center gap-2"
                    >
                        {loading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Plus className="h-3 w-3" />}
                        Add
                    </button>
                </div>
            </motion.form>
        </div>
    );
}

// ─── Opportunity card ──────────────────────────────────────────────
function OpportunityCard({ opp, client, onUpdate, onDelete }) {
    return (
        <motion.div
            layout
            data-testid={`opp-card-${opp.id}`}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            className="rounded-xl border border-white/[0.08] bg-white/[0.02] p-3 group"
        >
            <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium truncate">{opp.name}</div>
                    {client && (
                        <div className="mt-1 flex items-center gap-1 text-[10px] text-zinc-500">
                            <Building2 className="h-3 w-3" /> {client.name}
                        </div>
                    )}
                </div>
                <button
                    data-testid={`opp-delete-${opp.id}`}
                    onClick={() => onDelete(opp.id)}
                    className="opacity-0 group-hover:opacity-100 transition p-1 rounded hover:bg-white/[0.06]"
                    title="Delete"
                >
                    <Trash2 className="h-3 w-3 text-zinc-500" />
                </button>
            </div>
            <div className="mt-3 flex items-center justify-between">
                <span className="font-mono text-sm" style={{ color: STAGE_COLOR[opp.status] }}>
                    {currency(opp.value)}
                </span>
                <select
                    data-testid={`opp-status-${opp.id}`}
                    value={opp.status}
                    onChange={(e) => onUpdate(opp.id, { status: e.target.value })}
                    className="bg-transparent text-[10px] text-zinc-400 border border-white/[0.08] rounded px-1.5 py-0.5 focus:outline-none"
                >
                    {STAGES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
                </select>
            </div>
        </motion.div>
    );
}

// ─── Goal card ─────────────────────────────────────────────────────
function GoalCard({ goal, onOpen, onDelete }) {
    const days = daysUntil(goal.deadline);
    const p = goal.progress || {};
    const daysColor = days == null ? "text-zinc-500" : days < 0 ? "text-red-400" : days <= 7 ? "text-amber-400" : "text-zinc-300";
    return (
        <motion.div
            layout
            data-testid={`goal-card-${goal.id}`}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            whileHover={{ y: -3 }}
            className="elate-card p-6 relative group hover:border-white/20 transition"
        >
            <div className="flex items-start justify-between gap-4">
                <div className="min-w-0 flex-1">
                    <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Goal</div>
                    <h3 className="font-display text-xl mt-1.5 tracking-tight leading-tight">{goal.title}</h3>
                    <div className="mt-2 flex items-center gap-2 text-xs">
                        <Clock className={`h-3.5 w-3.5 ${daysColor}`} />
                        <span className={`${daysColor} font-mono`}>
                            {days == null ? "no deadline" : days < 0 ? `${Math.abs(days)}d over` : `${days}d left`}
                        </span>
                    </div>
                </div>

                <button
                    data-testid={`goal-delete-${goal.id}`}
                    onClick={(e) => { e.stopPropagation(); onDelete(goal.id); }}
                    className="opacity-0 group-hover:opacity-100 transition p-1.5 rounded-lg hover:bg-white/[0.06]"
                >
                    <Trash2 className="h-3.5 w-3.5 text-zinc-500" />
                </button>
            </div>

            {/* Progress ring + numbers */}
            <div className="mt-6 flex items-center gap-6">
                <div className="relative shrink-0">
                    <ProgressRing percent={p.percent || 0} size={120} />
                    <div className="absolute inset-0 grid place-items-center">
                        <div className="text-center">
                            <div className="font-display text-2xl font-medium">{p.percent || 0}%</div>
                            <div className="text-[10px] uppercase tracking-[0.15em] text-zinc-500 mt-0.5">Value won</div>
                        </div>
                    </div>
                </div>

                <div className="flex-1 space-y-3">
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Won</div>
                        <div className="font-mono text-lg">
                            {currency(p.won_value || 0)} <span className="text-zinc-500 text-xs">/ {currency(goal.target_value)}</span>
                        </div>
                    </div>
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Pipeline</div>
                        <div className="font-mono text-sm text-zinc-300">{currency(p.pipeline_value || 0)}</div>
                    </div>
                </div>
            </div>

            {/* Client count strip */}
            {goal.target_count && (
                <div className="mt-5 rounded-xl border border-white/[0.06] bg-white/[0.02] p-3">
                    <div className="flex items-center justify-between text-xs">
                        <span className="text-zinc-500">Clients won</span>
                        <span className="font-mono">
                            {p.counts?.won || 0} <span className="text-zinc-500">/ {goal.target_count}</span>
                            <span className="ml-2 text-zinc-500">{p.count_percent || 0}%</span>
                        </span>
                    </div>
                    <div className="mt-2 h-1.5 rounded-full bg-white/[0.05] overflow-hidden">
                        <motion.div
                            className="h-full bg-gradient-to-r from-[#0066FF] to-[#FF5500]"
                            initial={{ width: 0 }}
                            animate={{ width: `${p.count_percent || 0}%` }}
                            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                        />
                    </div>
                </div>
            )}

            {/* Counts strip */}
            <div className="mt-4 grid grid-cols-4 gap-2 text-center">
                {STAGES.map((s) => (
                    <div key={s.key} className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-2">
                        <div className="text-[10px] uppercase tracking-[0.15em] text-zinc-500">{s.label}</div>
                        <div className="font-mono text-sm mt-0.5" style={{ color: s.accent }}>
                            {p.counts?.[s.key] || 0}
                        </div>
                    </div>
                ))}
            </div>

            <button
                data-testid={`goal-open-${goal.id}`}
                onClick={() => onOpen(goal)}
                className="mt-5 w-full rounded-full border border-white/10 bg-white/[0.02] px-4 py-2 text-xs hover:bg-white/[0.05] transition"
            >
                Open pipeline →
            </button>
        </motion.div>
    );
}

// ─── Goal detail slide-over ────────────────────────────────────────
function GoalDetail({ goalId, onClose, onChanged, clients }) {
    const [goal, setGoal] = useState(null);
    const [loading, setLoading] = useState(true);
    const [showAdd, setShowAdd] = useState(false);

    const reload = async () => {
        try {
            const { data } = await api.get(`/goals/${goalId}`);
            setGoal(data);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (goalId) {
            setLoading(true);
            reload();
        }
        // eslint-disable-next-line
    }, [goalId]);

    const updateOpp = async (id, patch) => {
        // optimistic
        setGoal((g) => ({ ...g, opportunities: g.opportunities.map((o) => o.id === id ? { ...o, ...patch } : o) }));
        await api.patch(`/opportunities/${id}`, patch);
        await reload();
        onChanged?.();
    };

    const deleteOpp = async (id) => {
        setGoal((g) => ({ ...g, opportunities: g.opportunities.filter((o) => o.id !== id) }));
        await api.delete(`/opportunities/${id}`);
        await reload();
        onChanged?.();
    };

    const grouped = useMemo(() => {
        const g = { prospect: [], in_progress: [], won: [], lost: [] };
        (goal?.opportunities || []).forEach((o) => (g[o.status] || g.prospect).push(o));
        return g;
    }, [goal]);

    const clientMap = useMemo(
        () => Object.fromEntries((clients || []).map((c) => [c.id, c])),
        [clients]
    );

    if (!goalId) return null;

    return (
        <AnimatePresence>
            <motion.div
                className="fixed inset-0 z-50 flex"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={onClose}
            >
                <div className="absolute inset-0 bg-black/70 backdrop-blur-md" />
                <motion.aside
                    initial={{ x: "100%" }}
                    animate={{ x: 0 }}
                    exit={{ x: "100%" }}
                    transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                    onClick={(e) => e.stopPropagation()}
                    className="relative ml-auto w-full max-w-4xl bg-[#0A0A0C] border-l border-white/[0.08] h-full overflow-y-auto"
                    data-testid="goal-detail-panel"
                >
                    <div className="sticky top-0 backdrop-blur-xl bg-[#0A0A0C]/80 border-b border-white/[0.06] px-6 py-4 flex items-center justify-between">
                        <div className="min-w-0">
                            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Goal · pipeline</div>
                            <div className="font-display text-lg mt-0.5 truncate">{goal?.title || "…"}</div>
                        </div>
                        <div className="flex items-center gap-2">
                            <button
                                data-testid="opp-add-button"
                                onClick={() => setShowAdd(true)}
                                className="inline-flex items-center gap-1.5 rounded-full bg-white text-black px-3 py-1.5 text-xs font-medium hover:bg-zinc-200 transition"
                            >
                                <Plus className="h-3 w-3" /> Add opportunity
                            </button>
                            <button onClick={onClose} data-testid="goal-detail-close" className="p-2 rounded-lg hover:bg-white/[0.06]">
                                <X className="h-4 w-4" />
                            </button>
                        </div>
                    </div>

                    {loading || !goal ? (
                        <div className="p-6">
                            <div className="h-32 elate-card animate-pulse" />
                        </div>
                    ) : (
                        <div className="p-6 space-y-6">
                            {/* Summary strip */}
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                <div className="elate-card p-4">
                                    <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Progress</div>
                                    <div className="font-display text-2xl mt-1">{goal.progress.percent}%</div>
                                </div>
                                <div className="elate-card p-4">
                                    <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Won</div>
                                    <div className="font-mono text-lg mt-1 text-emerald-400">{currency(goal.progress.won_value)}</div>
                                </div>
                                <div className="elate-card p-4">
                                    <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Pipeline</div>
                                    <div className="font-mono text-lg mt-1">{currency(goal.progress.pipeline_value)}</div>
                                </div>
                                <div className="elate-card p-4">
                                    <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Deadline</div>
                                    <div className="font-mono text-lg mt-1">
                                        {daysUntil(goal.deadline) == null ? "—" : `${daysUntil(goal.deadline)}d`}
                                    </div>
                                </div>
                            </div>

                            {/* Kanban */}
                            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
                                {STAGES.map((s) => (
                                    <div key={s.key} data-testid={`opp-column-${s.key}`} className="elate-card p-3 min-h-[300px]">
                                        <div className="flex items-center justify-between px-2 py-2">
                                            <div className="flex items-center gap-2">
                                                <span className="h-1.5 w-1.5 rounded-full" style={{ background: s.accent }} />
                                                <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-400">{s.label}</div>
                                            </div>
                                            <span className="text-xs text-zinc-500 font-mono">{grouped[s.key].length}</span>
                                        </div>
                                        <div className="space-y-2 p-1">
                                            {grouped[s.key].map((o) => (
                                                <OpportunityCard
                                                    key={o.id}
                                                    opp={o}
                                                    client={clientMap[o.client_id]}
                                                    onUpdate={updateOpp}
                                                    onDelete={deleteOpp}
                                                />
                                            ))}
                                            {grouped[s.key].length === 0 && (
                                                <div className="text-xs text-zinc-600 text-center py-8">Empty</div>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </motion.aside>
            </motion.div>

            <CreateOppModal
                open={showAdd}
                onClose={() => setShowAdd(false)}
                goalId={goalId}
                clients={clients}
                onCreated={async () => { await reload(); onChanged?.(); }}
                onClientCreated={() => onChanged?.()}
            />
        </AnimatePresence>
    );
}

// ─── Page ──────────────────────────────────────────────────────────
export default function Goals() {
    const [goals, setGoals] = useState([]);
    const [clients, setClients] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showCreate, setShowCreate] = useState(false);
    const [openGoalId, setOpenGoalId] = useState(null);

    const load = async () => {
        try {
            const [g, c] = await Promise.all([api.get("/goals"), api.get("/clients")]);
            setGoals(g.data);
            setClients(c.data);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const remove = async (id) => {
        if (!window.confirm("Delete this goal and all its opportunities?")) return;
        setGoals((p) => p.filter((g) => g.id !== id));
        await api.delete(`/goals/${id}`);
    };

    return (
        <div className="space-y-6" data-testid="goals-page">
            <div className="flex items-end justify-between flex-wrap gap-4">
                <div>
                    <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Mission control</div>
                    <h2 className="font-display text-3xl mt-2">Goals</h2>
                </div>
                <button
                    data-testid="goal-new-button"
                    onClick={() => setShowCreate(true)}
                    className="inline-flex items-center gap-2 rounded-full bg-white text-black px-4 py-2 text-xs font-medium hover:bg-zinc-200 transition"
                >
                    <Plus className="h-3.5 w-3.5" /> New goal
                </button>
            </div>

            {loading ? (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                    {Array.from({ length: 2 }).map((_, i) => <div key={i} className="elate-card p-6 h-64 animate-pulse" />)}
                </div>
            ) : goals.length === 0 ? (
                <div className="elate-card p-12 text-center">
                    <Trophy className="h-8 w-8 text-zinc-500 mx-auto mb-3" />
                    <div className="font-display text-lg">No goals yet</div>
                    <div className="text-sm text-zinc-500 mt-1">Set one and start moving prospects into wins.</div>
                    <button
                        onClick={() => setShowCreate(true)}
                        className="mt-5 inline-flex items-center gap-2 rounded-full bg-white text-black px-4 py-2 text-xs font-medium hover:bg-zinc-200 transition"
                    >
                        <Plus className="h-3.5 w-3.5" /> Create first goal
                    </button>
                </div>
            ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                    {goals.map((g) => (
                        <GoalCard key={g.id} goal={g} onOpen={(x) => setOpenGoalId(x.id)} onDelete={remove} />
                    ))}
                </div>
            )}

            <CreateGoalModal open={showCreate} onClose={() => setShowCreate(false)} onCreated={(g) => setGoals((p) => [g, ...p])} />
            {openGoalId && (
                <GoalDetail
                    goalId={openGoalId}
                    clients={clients}
                    onClose={() => setOpenGoalId(null)}
                    onChanged={load}
                />
            )}
        </div>
    );
}
